import React, { useState } from 'react';
import FormLanguageSelector from '../common/FormLanguageSelector';
import { Button } from '../common/Button';
import { useAdminStore } from '../../stores/adminStore';
import { useTranslation } from 'react-i18next';
import { City } from '../../types/profile';
import { useCitySelector } from '../../hooks/useCitySelector';
import { PersonalInfoSection } from '../auth/forms/PersonalInfoSection';
import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAuth } from '../../context/auth';
import { validatePassword } from '../../utils/validation';
import { toast } from 'react-toastify';

const CreateAdminForm: React.FC = () => {
  const { t } = useTranslation(['admin', 'common', 'auth']);
  const { createAdminProfile } = useAdminStore();

  const [lastName, setLastName] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [phone, setPhone] = useState<string>('');

  const { doesEmailExist } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [show, setShow] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [errors, setErrors] = useState<{
    industries?: string;
    firstName?: string;
    lastName?: string;
    city?: string;
    phone?: string;
    languages?: string;
  }>({});

  // City store
  const {
    selectedOptions: selectedCities,
    setSelectedOptions: setSelectedCities,
    options: cityOptions,
  } = useCitySelector([]);

  const selectedCity = selectedCities[0];

  const setSelectedCity = (city: City | null) => {
    setSelectedCities(city ? [city] : []);
  };

  const isFormValid =
    firstName &&
    email &&
    password &&
    lastName &&
    selectedCity &&
    phone.trim() &&
    languages.length > 0;

  //Validation handlers
  const validateData = () => {
    const newErrors: typeof errors = {};
    if (!firstName.trim()) newErrors.firstName = t('fieldRequired');
    if (!lastName?.trim()) newErrors.lastName = t('fieldRequired');
    if (!selectedCity) newErrors.city = t('fieldRequired');
    if (!phone.trim()) newErrors.phone = t('fieldRequired');
    if (!languages.length) newErrors.languages = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateData()) return;
    setError(null);
    setSuccess(false);

    const errors = validatePassword(password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    } else {
      setPasswordErrors([]);
    }
    if (!isFormValid) {
      toast.error(t('errors.createAccount', { ns: 'auth' }));
      return;
    }

    const emailExist = await doesEmailExist(email);
    if (emailExist === true) {
      setEmailError(t('emailTakenException', { ns: 'auth' }));
      return;
    }

    setLoading(true);
    try {
      await createAdminProfile(
        email,
        password,
        firstName,
        lastName,
        languages,
        selectedCity.id,
        'administrator',
        undefined,
        phone
      );

      setSuccess(true);
      // clear form
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setLanguages([]);
      setSelectedCities([]);
      setPhone('');
    } catch (err) {
      console.error('Failed to create admin', err);
      setError((err as Error)?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm transition-all px-8">
      <form onSubmit={handleSubmit} className="space-y-4 md:p-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="mt-4">
            <label
              htmlFor="email"
              className="font-feature-settings:'liga'_off,'clig'_off] font-ambit text-[0.9375rem] font-normal leading-[1.25rem]"
            >
              {t('email', { ns: 'auth' })}
            </label>
            <div className="relative mt-2">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-secondary-light" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={`
                              block w-full pl-10 pr-3 py-3 rounded-md sm:text-sm
                              ${
                                emailError !== ''
                                  ? 'border border-high-contrast-error bg-high-contrast-warning'
                                  : 'border border-gray-300 bg-secondary-light'
                              }
                            `}
                placeholder={t('emailPlaceholder', { ns: 'auth' })}
                required
              />
            </div>
            {emailError && <p className="font-ambit mt-2 text-high-contrast-error">{emailError}</p>}
          </div>

          <div className="mt-4">
            <label htmlFor="password" className="font-ambit text-[0.9375rem] leading-[1.25rem]">
              {t('password', { ns: 'auth' })}
            </label>

            <div className="flex items-center mt-2 space-x-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-secondary-light" />
                </div>
                <input
                  id="password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className={`
                              block w-full pl-10 pr-3 py-3 border rounded-md sm:text-sm
                              ${
                                passwordErrors.length > 0
                                  ? 'border border-high-contrast-error bg-high-contrast-warning'
                                  : 'border-gray-300 bg-secondary-light'
                              }
                            `}
                  placeholder={t('passwordPlaceholder', { ns: 'auth' })}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-secondary-light"
                  onClick={() => setShow(v => !v)}
                  tabIndex={-1}
                >
                  {show ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {passwordErrors.length > 0 && (
                <AlertCircle className="text-red-500 w-5 h-5 shrink-0" />
              )}
            </div>

            {passwordErrors.length > 0 && (
              <ul className="mt-2 text-sm text-red-600 space-y-2">
                {passwordErrors.map((err, idx) => (
                  <li key={idx} className="flex items-start">
                    {t(`errors.${err}`, { ns: 'auth' })}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <PersonalInfoSection
          firstName={firstName}
          lastName={lastName}
          phone={phone}
          cityQuery={selectedCity?.name || ''}
          cityOptions={cityOptions}
          onFirstNameChange={(value: string) => setFirstName(value)}
          onLastNameChange={(value: string) => setLastName(value)}
          onPhoneChange={value => {
            setPhone(value);
            if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
          }}
          onCitySelect={setSelectedCity}
          errors={errors}
        />

        <FormLanguageSelector
          value={languages}
          onChange={setLanguages}
          error={errors.languages}
          required
        />

        {error && <div className="text-high-contrast-error">{error}</div>}
        {success && (
          <div className="text-green-600">
            {t('create_admin_success', {
              ns: 'admin',
            })}
          </div>
        )}

        <div className="pt-8">
          <Button type="submit" disabled={loading} className="px-6">
            {loading
              ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
              : t('create_admin', { ns: 'admin', defaultValue: 'Ajouter un administrateur' })}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateAdminForm;
